import { useEffect, useMemo, useState } from "react";
import { downloadReport, reportToMarkdown } from "../reportExport";
import { buildSimpleAssessment, type SimpleAssessment } from "../simpleAssessment";
import {
  assuranceLabel,
  impactLabel,
  shortAddress,
  statusLabel,
  type AnalysisBundle,
  type AssuranceLevel,
  type CapabilityFinding,
  type DumpCapacityReport,
  type ExecutableExitReport,
  type ExitLadderRow,
  type IdentityReport,
  type LiquidityControlAnalysis,
  type RugCapacityReport,
  type TokenIdentity,
} from "../shared/report";

type SectionState = "idle" | "loading" | "ready" | "error";

interface ReportViewProps {
  mode: "fixture" | "live";
  fixtureLabel?: string | null;
  identity: TokenIdentity;
  identityReport: IdentityReport | null;
  rugCapacity: RugCapacityReport | null;
  dumpCapacity: DumpCapacityReport | null;
  executableExit: ExecutableExitReport | null;
  identityState: SectionState;
  capabilityState: SectionState;
  dumpState: SectionState;
  exitState: SectionState;
  capabilityError?: string | null;
  dumpError?: string | null;
  exitError?: string | null;
  completeReport?: AnalysisBundle | null;
  onRefreshLive?: () => void;
}

export function ReportView({
  mode,
  fixtureLabel,
  identity,
  identityReport,
  rugCapacity,
  dumpCapacity,
  executableExit,
  identityState,
  capabilityState,
  dumpState,
  exitState,
  capabilityError,
  dumpError,
  exitError,
  completeReport,
  onRefreshLive,
}: ReportViewProps) {
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [showFullEvidence, setShowFullEvidence] = useState(false);
  const name = identity.name || "Metadata unavailable";
  const symbol = identity.symbol ? ` ${identity.symbol}` : "";
  const observed = rugCapacity?.findings.filter((finding) => finding.status === "observed").length ?? 0;
  const unknown = rugCapacity?.findings.filter((finding) => finding.status === "unknown").length ?? 0;
  const notAssessed = rugCapacity?.findings.filter((finding) => finding.status === "not_assessed").length ?? 0;
  const rowsWithProceeds =
    executableExit?.rows.filter((row) => row.expectedOutputAssetDelta !== null).length ?? 0;
  const simpleAssessment = useMemo(
    () =>
      buildSimpleAssessment({
        identityReport,
        rugCapacity,
        dumpCapacity,
        executableExit,
        identityState,
        capabilityState,
        dumpState,
        exitState,
      }),
    [identityReport, rugCapacity, dumpCapacity, executableExit, identityState, capabilityState, dumpState, exitState],
  );

  useEffect(() => {
    setShowFullEvidence(false);
  }, [identity.address]);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(identity.address);
      setCopyMessage("Address copied");
    } catch {
      setCopyMessage("Copy unavailable—select the full address below");
    }
  };

  const copyReport = async () => {
    if (!completeReport) return;
    try {
      await navigator.clipboard.writeText(reportToMarkdown(completeReport));
      setCopyMessage("Plain-language report copied");
    } catch {
      setCopyMessage("Copy unavailable; download the Markdown report instead");
    }
  };

  return (
    <article className="report-shell" aria-labelledby="report-title">
      <div className={`report-mode-banner report-mode-${mode}`}>
        <span className="report-mode-icon" aria-hidden="true">{mode === "fixture" ? "◇" : "●"}</span>
        <div>
          <strong>{mode === "fixture" ? "MADE-UP DEMO" : "LIVE OBSERVATION"}</strong>
          <span>
            {mode === "fixture"
              ? `${fixtureLabel ?? "Made-up scenario"} · never a live token`
              : "Current mainnet reads · perishable evidence, not a safety guarantee"}
          </span>
        </div>
      </div>

      <header className="report-header">
        <div className="token-identity">
          <div className="identity-kicker-row">
            <p className="eyebrow">
              {identity.network === "Solana mainnet-beta" ? "Solana mainnet" : identity.network}
            </p>
            {identityReport && (
              <span className={`identity-status identity-status-${identityReport.status}`}>
                {identityReport.label}
              </span>
            )}
          </div>
          <h2 id="report-title">
            {name}
            {symbol && <span className="token-symbol">{symbol}</span>}
          </h2>
          <div className="address-display">
            <code title={identity.address}>{identity.address}</code>
            <button type="button" className="copy-button" onClick={copyAddress}>
              Copy address
            </button>
          </div>
          <p className="metadata-warning">{identity.metadataWarning}</p>
        </div>
      </header>

      <SimpleAssessmentCard
        assessment={simpleAssessment}
        showFullEvidence={showFullEvidence}
        onToggleEvidence={() => setShowFullEvidence((current) => !current)}
        onRefreshLive={mode === "live" ? onRefreshLive : undefined}
      />

      <span
        className={copyMessage ? "report-copy-status" : "sr-only"}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {copyMessage ?? ""}
      </span>

      {showFullEvidence && (
        <div className="evidence-layer" id="full-evidence">
          <div className="report-actions">
            {completeReport && (
              <>
                <button className="button button-quiet" type="button" onClick={copyReport}>
                  Copy summary
                </button>
                <button
                  className="button button-quiet"
                  type="button"
                  onClick={() => downloadReport(completeReport, "markdown")}
                >
                  Download .md
                </button>
                <button
                  className="button button-quiet"
                  type="button"
                  onClick={() => downloadReport(completeReport, "json")}
                >
                  Download JSON
                </button>
              </>
            )}
          </div>

          <section className="technical-identity" aria-labelledby="technical-identity-title">
            <p className="eyebrow" id="technical-identity-title">Technical token details</p>
            <dl className="identity-facts">
              <div>
                <dt>Program</dt>
                <dd>{identity.tokenProgram}</dd>
              </div>
              <div>
                <dt>Decimals</dt>
                <dd>{identity.decimals}</dd>
              </div>
              <div>
                <dt>Network ID</dt>
                <dd title={identity.networkIdentity}>{shortAddress(identity.networkIdentity, 6, 6)}</dd>
              </div>
              <div>
                <dt>Asset type</dt>
                <dd>{assetTypeLabel(identity.assetType)}</dd>
              </div>
            </dl>
          </section>

          <div className="summary-rail" role="group" aria-label="Report at a glance">
            <SummaryMetric
              label="Exact-address identity"
              value={identityState === "ready" ? identityMetric(identityReport) : "…"}
              detail={identityState === "ready" ? identityReport?.label ?? "unavailable" : sectionStatus(identityState)}
              tone={identityReport?.status === "registry_recognized" ? "calm" : "unknown"}
            />
            <SummaryMetric
              label="Controller powers"
              value={capabilityState === "ready" ? String(observed) : "…"}
              detail={capabilityState === "ready" ? controllerGapMetric(unknown, notAssessed) : sectionStatus(capabilityState)}
              tone={observed ? "danger" : unknown || notAssessed ? "unknown" : "calm"}
            />
            <SummaryMetric
              label="Largest wallet-shaped holder"
              value={dumpState === "ready" ? dumpMetric(dumpCapacity) : "…"}
              detail={dumpState === "ready" ? dumpCapacity?.riskBand.replaceAll("_", " ") ?? "unavailable" : sectionStatus(dumpState)}
              tone={dumpCapacity?.riskBand === "high" || dumpCapacity?.riskBand === "elevated" ? "warning" : dumpCapacity?.riskBand === "unknown" ? "unknown" : "calm"}
            />
            <SummaryMetric
              label="Exit sizes with proceeds"
              value={exitState === "ready" ? `${rowsWithProceeds}/${executableExit?.rows.length ?? 0}` : "…"}
              detail={exitState === "ready" ? strongestAssurance(executableExit?.rows ?? []) : sectionStatus(exitState)}
              tone={exitTone(executableExit)}
            />
          </div>

          <section className="report-section identity-section" aria-labelledby="identity-evidence-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow section-number">Identity · registry check</p>
                <h2 id="identity-evidence-title">Is this the token you meant?</h2>
              </div>
              <p>
                Jupiter and CoinGecko can recognize an exact mint. Each source is named precisely;
                age, volume, holders, ticker, and logo never prove identity.
              </p>
            </div>
            {identityState === "loading" && <SectionLoading label="Checking exact-address provenance" />}
            {identityState === "error" && <SectionError title="Identity evidence is Unknown" message={capabilityError} />}
            {identityReport && <IdentityEvidence report={identityReport} />}
          </section>

          <section className="report-section" aria-labelledby="rug-capacity-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow section-number">Rug capacity · controller risk</p>
                <h2 id="rug-capacity-title">Which dangerous powers are active?</h2>
              </div>
              <p>
                An observed power is real technical capacity; it does not prove intent. A clear row
                applies only to the checks named in its coverage.
              </p>
            </div>
            {capabilityState === "loading" && (
              <SectionLoading label="Reading mint controls and Token-2022 behavior" />
            )}
            {capabilityState === "error" && (
              <SectionError title="Controller analysis is Unknown" message={capabilityError} />
            )}
            {rugCapacity && <CapabilityReport report={rugCapacity} />}
          </section>

          <section className="report-section dump-section" aria-labelledby="dump-capacity-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow section-number">Dump capacity · concentration risk</p>
                <h2 id="dump-capacity-title">Could a large holder overwhelm buyers?</h2>
              </div>
              <p>
                The largest token accounts are grouped by owner and classified before their share
                is shown. Wallet-shaped does not mean insider; program-controlled does not mean safe.
              </p>
            </div>
            {dumpState === "loading" && <SectionLoading label="Grouping and classifying the largest holder accounts" />}
            {dumpState === "error" && <SectionError title="Dump-capacity evidence is Unknown" message={dumpError} />}
            {dumpCapacity && <DumpCapacityEvidence report={dumpCapacity} />}
          </section>

          <section className="report-section exit-section" aria-labelledby="exit-liquidity-title">
            <div className="section-heading">
              <div>
                <p className="eyebrow section-number">Exit liquidity · market risk</p>
                <h2 id="exit-liquidity-title">What does each sell size quote?</h2>
              </div>
              <p>
                Every size gets its own result. A $100 route does not prove a $10,000 exit; the
                larger position may lose most of its value or find no route.
              </p>
            </div>
            {exitState === "loading" && (
              <SectionLoading label="Checking the exit ladder at each size" slower />
            )}
            {exitState === "error" && (
              <SectionError title="Exit evidence is Unknown" message={exitError} />
            )}
            {executableExit && <ExitReport report={executableExit} />}
          </section>

          {(identityReport || rugCapacity || dumpCapacity || executableExit) && (
            <section className="method-section" aria-labelledby="method-title">
              <div>
                <p className="eyebrow section-number">Coverage and limits</p>
                <h2 id="method-title">Where this report stops</h2>
              </div>
              <div className="method-grid">
                <div>
                  <h3>Identity-analysis limits</h3>
                  <ul>{identityReport?.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
                <div>
                  <h3>Controller-analysis limits</h3>
                  <ul>{rugCapacity?.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
                <div>
                  <h3>Dump-capacity limits</h3>
                  <ul>{dumpCapacity?.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
                <div>
                  <h3>Exit-analysis limits</h3>
                  <ul>{executableExit?.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
                </div>
              </div>
              <p className="methodology-version">
                Network: {identity.network}. Methodology {identityReport?.methodologyVersion ?? rugCapacity?.methodologyVersion ?? dumpCapacity?.methodologyVersion ?? executableExit?.methodologyVersion}.
                This is decision support, not financial advice, an accusation, or a guarantee.
              </p>
            </section>
          )}
        </div>
      )}
    </article>
  );
}

function SimpleAssessmentCard({
  assessment,
  showFullEvidence,
  onToggleEvidence,
  onRefreshLive,
}: {
  assessment: SimpleAssessment;
  showFullEvidence: boolean;
  onToggleEvidence: () => void;
  onRefreshLive?: () => void;
}) {
  return (
    <section className={`simple-assessment simple-assessment-${assessment.tone}`} aria-labelledby="simple-assessment-title">
      <div className="simple-assessment-heading">
        <div>
          <p className="eyebrow">Bestie translation</p>
          <h2 id="simple-assessment-title">{assessment.headline}</h2>
        </div>
        <span className={`simple-verdict simple-verdict-${assessment.tone}`}>{assessment.label}</span>
      </div>
      <p className="simple-assessment-summary">{assessment.summary}</p>

      <div className="simple-check-grid" role="group" aria-label="The four practical checks">
        {assessment.checks.map((check) => {
          const state = simpleCheckState(check.tone);
          return (
            <article className={`simple-check simple-check-${check.tone}`} key={check.number}>
              <div className="simple-check-heading">
                <span>{check.number}</span>
                <div>
                  <h3>{check.title}</h3>
                  <p>{check.question}</p>
                </div>
                <span className="simple-check-state" aria-label={`${state.label} result`}>
                  <span aria-hidden="true">{state.mark}</span>
                  {state.label}
                </span>
              </div>
              <strong>{check.result}</strong>
            </article>
          );
        })}
      </div>

      <div className="simple-reasons">
        <p className="eyebrow">The receipts</p>
        <ul>{assessment.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
      </div>
      <p className="simple-caveat">{assessment.caveat}</p>

      <div className="simple-actions">
        {onRefreshLive && (
          <button className="button button-secondary" type="button" onClick={onRefreshLive}>
            Refresh live evidence
          </button>
        )}
        <button
          className="button button-primary"
          type="button"
          aria-expanded={showFullEvidence}
          aria-controls="full-evidence"
          onClick={onToggleEvidence}
        >
          {showFullEvidence ? "Hide full evidence" : "Show full evidence"}
        </button>
      </div>
    </section>
  );
}

function IdentityEvidence({ report }: { report: IdentityReport }) {
  return (
    <>
      <div className={`section-callout identity-callout identity-callout-${report.status}`}>
        <span className="callout-mark" aria-hidden="true">
          {report.status === "registry_recognized" ? "≡" : "?"}
        </span>
        <div>
          <strong>{report.headline}</strong>
          <p>{report.plainSummary[0]}</p>
        </div>
      </div>
      <div className="identity-evidence-grid">
        <div>
          <span>Result</span>
          <strong>{report.label}</strong>
        </div>
        <div>
          <span>Recognized mint</span>
          <strong title={report.recognizedAddress ?? undefined}>
            {report.recognizedAddress ? shortAddress(report.recognizedAddress, 7, 7) : "Not established"}
          </strong>
        </div>
        <div>
          <span>Matched using</span>
          <strong>{report.matchedFields.join(" · ")}</strong>
        </div>
        <div>
          <span>Registry evidence</span>
          {report.sources.length ? (
            <div className="identity-source-list">
              {report.sources.map((source, index) => (
                source.url ? (
                  <a href={source.url} target="_blank" rel="noreferrer" key={`${source.provider}-${index}`}>
                    {source.label} ↗
                  </a>
                ) : (
                  <strong key={`${source.provider}-${index}`}>{source.label}</strong>
                )
              ))}
            </div>
          ) : report.status === "unknown" ? (
            <strong>One or more registry checks did not complete</strong>
          ) : (
            <strong>No positive exact-mint source</strong>
          )}
        </div>
      </div>
      <details className="coverage-details">
        <summary>Exactly how identity was checked</summary>
        <ul>{report.coverage.map((item) => <li key={item}>{item}</li>)}</ul>
      </details>
    </>
  );
}

function simpleCheckState(tone: SimpleAssessment["tone"]): {
  mark: string;
  label: string;
} {
  const states: Record<SimpleAssessment["tone"], { mark: string; label: string }> = {
    calm: { mark: "✓", label: "CLEAR" },
    warning: { mark: "▲", label: "CAUTION" },
    danger: { mark: "!", label: "DANGER" },
    unknown: { mark: "?", label: "UNKNOWN" },
    not_assessed: { mark: "—", label: "NOT ASSESSED" },
    checking: { mark: "◌", label: "CHECKING" },
  };
  return states[tone];
}

function DumpCapacityEvidence({ report }: { report: DumpCapacityReport }) {
  return (
    <>
      <div className={`section-callout dump-callout dump-callout-${report.riskBand}`}>
        <span className="callout-mark" aria-hidden="true">⇣</span>
        <div>
          <strong>{report.headline}</strong>
          <p>{report.plainSummary[2] ?? report.plainSummary[0]}</p>
        </div>
      </div>
      <div className="dump-stat-grid">
        <div><span>Largest wallet-shaped</span><strong>{percentageOrUnknown(report.largestWalletShapedSharePercentage)}</strong></div>
        <div><span>Wallet-shaped in top set</span><strong>{formatPercentage(report.walletShapedSharePercentage)}</strong></div>
        <div><span>Program-controlled</span><strong>{formatPercentage(report.programControlledSharePercentage)}</strong></div>
        <div><span>Unresolved owners</span><strong>{formatPercentage(report.unresolvedSharePercentage)}</strong></div>
      </div>
      {report.holders.length ? (
        <details className="coverage-details holder-details">
          <summary>Inspect the largest grouped owners</summary>
          <div className="holder-table-wrap">
            <table className="holder-table">
              <caption>Largest token accounts grouped by parsed owner authority</caption>
              <thead><tr><th scope="col">Rank</th><th scope="col">Owner</th><th scope="col">Type</th><th scope="col">Supply</th><th scope="col">Token amount</th></tr></thead>
              <tbody>
                {report.holders.slice(0, 12).map((holder) => (
                  <tr key={`${holder.authority ?? "unknown"}-${holder.rank}`}>
                    <td>{holder.rank}</td>
                    <td>
                      {holder.authority && holder.sourceUrl ? (
                        <a href={holder.sourceUrl} target="_blank" rel="noreferrer">{shortAddress(holder.authority, 6, 6)} ↗</a>
                      ) : holder.authority ? (
                        <code>{shortAddress(holder.authority, 6, 6)}</code>
                      ) : (
                        "Unresolved"
                      )}
                    </td>
                    <td>{holderKindLabel(holder.authorityKind)}</td>
                    <td>{formatPercentage(holder.shareOfSupplyPercentage)}</td>
                    <td>{compactTokenAmount(holder.tokenAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      ) : (
        <p className="muted">No holder rows were available for this result.</p>
      )}
      <details className="coverage-details">
        <summary>Exactly what this concentration scan checked</summary>
        <ul>{report.coverage.map((item) => <li key={item}>{item}</li>)}</ul>
      </details>
    </>
  );
}

function CapabilityReport({ report }: { report: RugCapacityReport }) {
  return (
    <>
      <div className="section-callout">
        <span className="callout-mark" aria-hidden="true">⌁</span>
        <div>
          <strong>{report.headline}</strong>
          <p>{report.plainSummary[2] ?? "Technical capability is different from intent."}</p>
        </div>
      </div>
      <div className="capability-grid">
        {report.findings.map((finding) => <CapabilityCard key={finding.id} finding={finding} />)}
      </div>
      <details className="coverage-details">
        <summary>Exactly what this controller scan checked</summary>
        <ul>{report.coverage.map((item) => <li key={item}>{item}</li>)}</ul>
      </details>
    </>
  );
}

function CapabilityCard({ finding }: { finding: CapabilityFinding }) {
  return (
    <article className={`capability-card capability-${finding.status}${finding.liquidityControl ? " capability-liquidity" : ""}`}>
      <div className="capability-heading">
        <span className={`finding-icon finding-icon-${finding.status}`} aria-hidden="true">
          {finding.status === "observed"
            ? "!"
            : finding.status === "unknown"
              ? "?"
              : finding.status === "not_assessed"
                ? "—"
                : "✓"}
        </span>
        <div>
          <p>{finding.plainQuestion}</p>
          <h3>{finding.title}</h3>
        </div>
      </div>
      <span className={`finding-status finding-status-${finding.status}`}>{statusLabel(finding.status)}</span>
      <p className="finding-summary">{finding.summary}</p>
      <dl className="finding-facts">
        <div>
          <dt>Potential impact</dt>
          <dd>{impactLabel(finding.impactClass)}</dd>
        </div>
        <div>
          <dt>Estimated exposure</dt>
          <dd>{finding.estimatedExposure}</dd>
        </div>
        <div>
          <dt>Controller</dt>
          <dd title={finding.control.controller ?? undefined}>
            {finding.control.controller ? shortAddress(finding.control.controller, 6, 6) : "Not identified"}
          </dd>
        </div>
        <div>
          <dt>Authority type</dt>
          <dd>{controlKindLabel(finding.control.kind)}</dd>
        </div>
        <div>
          <dt>Constraint</dt>
          <dd>{finding.control.minimumCoalition}</dd>
        </div>
        <div>
          <dt>Owning program</dt>
          <dd title={finding.control.controllingProgram ?? undefined}>
            {finding.control.controllingProgram
              ? shortAddress(finding.control.controllingProgram, 6, 6)
              : "Not established"}
          </dd>
        </div>
      </dl>
      {finding.liquidityControl && (
        <LiquidityControlReceipt analysis={finding.liquidityControl} />
      )}
      <details className="finding-details">
        <summary>Why this matters &amp; evidence</summary>
        <div className="finding-detail-body">
          <p><strong>Why it matters.</strong> {finding.whyItMatters}</p>
          <p><strong>Warning delay.</strong> {finding.control.warningDelay}</p>
          <p><strong>Coverage.</strong> {finding.coverage}</p>
          {finding.control.notes && <p><strong>Control note.</strong> {finding.control.notes}</p>}
          {finding.evidence.length ? (
            <div className="evidence-list">
              {finding.evidence.map((evidence) => (
                <div className="evidence-item" key={evidence.id}>
                  <div>
                    <strong>{evidence.label}</strong>
                    <span>{evidence.field}</span>
                  </div>
                  <p>{evidence.interpretation}</p>
                  <code>{boundedJson(evidence.rawValue)}</code>
                  <div className="evidence-meta">
                    <span>{evidence.context.provider}</span>
                    <span>{evidence.context.slot ? `slot ${evidence.context.slot.toLocaleString("en-US")}` : "slot unavailable"}</span>
                    {evidence.sourceUrl && (
                      <a href={evidence.sourceUrl} target="_blank" rel="noreferrer">Open explorer ↗</a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No material evidence item was captured for this row.</p>
          )}
        </div>
      </details>
    </article>
  );
}

function LiquidityControlReceipt({
  analysis,
}: {
  analysis: LiquidityControlAnalysis;
}) {
  return (
    <section className="liquidity-control-receipt" aria-label="Liquidity controller receipt">
      <div className="liquidity-receipt-heading">
        <div>
          <span className="eyebrow">Bounded pool receipt</span>
          <strong>{analysis.poolType ?? "No eligible pool selected"}</strong>
        </div>
        {analysis.poolUrl && (
          <a href={analysis.poolUrl} target="_blank" rel="noreferrer">
            Open pool ↗
          </a>
        )}
      </div>

      <dl className="liquidity-receipt-metrics">
        <div>
          <dt>Visible pool liquidity</dt>
          <dd>{analysis.liquidityUsd === null ? "Unavailable" : compactUsd(analysis.liquidityUsd)}</dd>
        </div>
        <div>
          <dt>Largest direct controller</dt>
          <dd>{percentageOrUnknown(analysis.largestDirectControllerSharePercentage)}</dd>
        </div>
        <div>
          <dt>Provably burned LP claim</dt>
          <dd>{percentageOrUnknown(analysis.burnedLpSharePercentage)}</dd>
        </div>
        <div>
          <dt>Top accounts / live LP</dt>
          <dd>{percentageOrUnknown(analysis.topAccountsCoveragePercentage)}</dd>
        </div>
        <div>
          <dt>Opaque live claim</dt>
          <dd>{percentageOrUnknown(analysis.opaqueLiveClaimPercentage)}</dd>
        </div>
        <div>
          <dt>Pool withdrawal flag</dt>
          <dd>
            {analysis.withdrawalsCurrentlyEnabled === null
              ? "Flag not decoded"
              : analysis.withdrawalsCurrentlyEnabled
                ? "Enabled"
                : "Disabled"}
          </dd>
        </div>
      </dl>
      {analysis.effectiveClaimCoveragePercentage !== null && (
        <p className="liquidity-coverage-note">
          Burned LP plus decoded top accounts cover {formatPercentage(analysis.effectiveClaimCoveragePercentage)} of this pool's withdrawal base.
        </p>
      )}

      {analysis.coalitionThresholds.length > 0 && (
        <div className="coalition-strip" aria-label="Smallest direct-controller address coalition">
          <span>Smallest direct-address coalition</span>
          <div>
            {analysis.coalitionThresholds.map((threshold) => (
              <span key={threshold.targetPercentage}>
                <strong>{threshold.targetPercentage}%</strong>
                {threshold.minimumControllerAddresses === null
                  ? "not reached"
                  : `${threshold.minimumControllerAddresses} ${threshold.minimumControllerAddresses === 1 ? "address" : "addresses"}`}
              </span>
            ))}
          </div>
          <small>Separate addresses are not assumed to coordinate or share an owner.</small>
        </div>
      )}

      {analysis.controllers.length > 0 && (
        <div className="liquidity-controller-table-wrap">
          <table className="liquidity-controller-table">
            <caption>Largest live LP claims grouped by parsed owner authority</caption>
            <thead>
              <tr><th scope="col">Rank</th><th scope="col">Controller</th><th scope="col">Type</th><th scope="col">Pool claim</th></tr>
            </thead>
            <tbody>
              {analysis.controllers.slice(0, 8).map((controller) => (
                <tr key={`${controller.authority ?? "unresolved"}-${controller.rank}`}>
                  <td>{controller.rank}</td>
                  <td>
                    {controller.authority ? (
                      controller.sourceUrl ? (
                        <a href={controller.sourceUrl} target="_blank" rel="noreferrer">
                          {shortAddress(controller.authority, 6, 6)} ↗
                        </a>
                      ) : (
                        shortAddress(controller.authority, 6, 6)
                      )
                    ) : (
                      "Unresolved"
                    )}
                    <small className="liquidity-controller-kind-mobile">
                      {liquidityControllerKindLabel(controller.authorityKind)}
                    </small>
                  </td>
                  <td className="liquidity-controller-kind-cell">
                    {liquidityControllerKindLabel(controller.authorityKind)}
                  </td>
                  <td className="liquidity-controller-claim-cell">
                    {formatPercentage(controller.shareOfWithdrawalBasePercentage)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="liquidity-relationship-note">
        <strong>Creator relationship:</strong> not checked
        {analysis.poolCreatorAddress
          ? ` · pool creator ${shortAddress(analysis.poolCreatorAddress, 6, 6)} is shown as an address only`
          : " · no creator link is inferred from LP ownership"}
      </p>
    </section>
  );
}

function ExitReport({ report }: { report: ExecutableExitReport }) {
  const currentAssurance = strongestAssuranceKey(report.rows);
  return (
    <>
      <div className="reference-strip">
        <div>
          <span className="eyebrow">Independent size reference</span>
          <strong>
            {report.reference.status === "available" && report.reference.priceUsd !== null
              ? `${usd(report.reference.priceUsd, 8)} per token`
              : "USD reference unavailable"}
          </strong>
          <p>{report.reference.confidenceNote}</p>
        </div>
        <dl>
          <div>
            <dt>Source</dt>
            <dd>{report.reference.source}</dd>
          </div>
          <div>
            <dt>Pool context</dt>
            <dd>{report.reference.liquidityUsd === null ? "Unavailable" : usd(report.reference.liquidityUsd, 0)}</dd>
          </div>
        </dl>
        {report.reference.pairUrl && (
          <a className="quiet-link" href={report.reference.pairUrl} target="_blank" rel="noreferrer">
            View reference pair ↗
          </a>
        )}
      </div>

      <div className="section-callout exit-callout">
        <span className="callout-mark" aria-hidden="true">↘</span>
        <div>
          <strong>{report.headline}</strong>
          <p>{report.plainSummary[1]}</p>
        </div>
      </div>

      {report.rows.length ? (
        <>
          <ExitCurve rows={report.rows} />
          <div className="exit-table-wrap">
            <table className="exit-table">
              <caption>Size-by-size exit evidence</caption>
              <thead>
                <tr>
                  <th scope="col">Test size</th>
                  <th scope="col">Token amount</th>
                  <th scope="col">Expected USDC</th>
                  <th scope="col">Quoted recovery</th>
                  <th scope="col">Assurance</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => <ExitTableRow key={row.id} row={row} />)}
              </tbody>
            </table>
          </div>
          <div className="exit-mobile-list">
            {report.rows.map((row) => <ExitMobileCard key={row.id} row={row} />)}
          </div>
        </>
      ) : (
        <div className="empty-exit-state">
          <strong>No dollar ladder was run.</strong>
          <p>
            Rug Lens could not find an independent reference price, so it refused to size the tests
            from the same route being evaluated. Use raw token amounts in Optional test sizes.
          </p>
        </div>
      )}

      <div className="assurance-explainer">
        <div>
          <p className="eyebrow">Assurance ladder</p>
          <h3>A quote is not a simulated sale</h3>
          <p>{report.simulationNote}</p>
        </div>
        <ol>
          <li className={currentAssurance === "pool_estimate" ? "is-current" : undefined}><span>1</span> Pool estimate</li>
          <li className={currentAssurance === "aggregator_quote" ? "is-current" : undefined}><span>2</span> Aggregator quote</li>
          <li className={currentAssurance === "route_simulated" ? "is-current" : undefined}><span>3</span> Route simulated</li>
          <li className={currentAssurance === "synthetic_simulated" ? "is-current" : undefined}><span>4</span> Synthetic simulation</li>
          <li className={currentAssurance === "holder_context_simulated" ? "is-current" : undefined}><span>5</span> Holder-context simulated</li>
        </ol>
      </div>

      <details className="coverage-details">
        <summary>Pool check and quote caveats</summary>
        <p>{report.directPoolCheck}</p>
        <ul>{report.limitations.map((item) => <li key={item}>{item}</li>)}</ul>
      </details>
    </>
  );
}

function ExitCurve({ rows }: { rows: ExitLadderRow[] }) {
  const points = useMemo(
    () =>
      rows
        .map((row, index) => ({
          row,
          index,
          recovery: row.estimatedRecoveryPercentage,
        }))
        .filter((point): point is { row: ExitLadderRow; index: number; recovery: number } => point.recovery !== null),
    [rows],
  );
  if (!points.length) return null;

  const width = 720;
  const height = 230;
  const left = 56;
  const right = 28;
  const top = 28;
  const bottom = 48;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const xFor = (index: number) =>
    left + (rows.length === 1 ? plotWidth / 2 : (index / (rows.length - 1)) * plotWidth);
  const yFor = (recovery: number) => top + (1 - Math.min(Math.max(recovery, 0), 105) / 105) * plotHeight;
  let pathOpen = false;
  const path = rows
    .map((row, index) => {
      if (row.estimatedRecoveryPercentage === null) {
        pathOpen = false;
        return "";
      }
      const command = pathOpen ? "L" : "M";
      pathOpen = true;
      return `${command} ${xFor(index)} ${yFor(row.estimatedRecoveryPercentage)}`;
    })
    .filter(Boolean)
    .join(" ");

  return (
    <figure className="exit-curve">
      <figcaption>
        <strong>Quoted recovery by requested size</strong>
        <span>Before unknown network costs; gaps mean no comparable recovery value.</span>
      </figcaption>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Quoted recovery percentage across requested exit sizes">
        {[0, 50, 100].map((tick) => (
          <g key={tick}>
            <line x1={left} y1={yFor(tick)} x2={width - right} y2={yFor(tick)} className="chart-grid" />
            <text x={left - 10} y={yFor(tick) + 4} textAnchor="end" className="chart-label">{tick}%</text>
          </g>
        ))}
        <path d={path} className="chart-line" />
        {points.map((point) => (
          <g key={point.row.id}>
            <circle cx={xFor(point.index)} cy={yFor(point.recovery)} r="7" className={`chart-point chart-point-${exitRowTone(point.row)}`} />
            <text x={xFor(point.index)} y={height - 17} textAnchor="middle" className="chart-label">
              {point.row.requestedUsd === null ? `${compactNumber(Number(point.row.inputTokenAmount))} tok` : compactUsd(point.row.requestedUsd)}
            </text>
            <text x={xFor(point.index)} y={yFor(point.recovery) - 13} textAnchor="middle" className="chart-value">
              {point.recovery.toFixed(1)}%
            </text>
          </g>
        ))}
      </svg>
    </figure>
  );
}

function ExitTableRow({ row }: { row: ExitLadderRow }) {
  return (
    <tr className={`exit-row exit-row-${exitRowTone(row)}`}>
      <th scope="row">
        <span className="exit-state-mark" aria-hidden="true">{exitStateMark(row)}</span>
        <span>{row.requestedUsd === null ? `${compactNumber(Number(row.inputTokenAmount))} tokens` : usd(row.requestedUsd)}</span>
      </th>
      <td title={row.inputTokenAmount}>{compactNumber(Number(row.inputTokenAmount))}</td>
      <td>{row.expectedOutputAssetDelta === null ? "—" : usd(row.expectedOutputAssetDelta)}</td>
      <td>
        {row.estimatedRecoveryPercentage === null ? (
          <span className="muted">Unavailable</span>
        ) : (
          <strong>{row.estimatedRecoveryPercentage.toFixed(1)}%</strong>
        )}
      </td>
      <td>
        <span className={`assurance-pill assurance-${row.assurance}`}>{assuranceLabel(row.assurance)}</span>
        <details className="row-details">
          <summary>Details</summary>
          <ExitRowDetails row={row} />
        </details>
      </td>
    </tr>
  );
}

function ExitMobileCard({ row }: { row: ExitLadderRow }) {
  return (
    <article className={`exit-mobile-card exit-row-${exitRowTone(row)}`}>
      <div className="exit-mobile-heading">
        <span className="exit-state-mark" aria-hidden="true">{exitStateMark(row)}</span>
        <div>
          <span>Test size</span>
          <strong>{row.requestedUsd === null ? `${compactNumber(Number(row.inputTokenAmount))} tokens` : usd(row.requestedUsd)}</strong>
        </div>
        <span className={`assurance-pill assurance-${row.assurance}`}>{assuranceLabel(row.assurance)}</span>
      </div>
      <dl>
        <div><dt>Expected USDC</dt><dd>{row.expectedOutputAssetDelta === null ? "Unavailable" : usd(row.expectedOutputAssetDelta)}</dd></div>
        <div><dt>Quoted recovery</dt><dd>{row.estimatedRecoveryPercentage === null ? "Unavailable" : `${row.estimatedRecoveryPercentage.toFixed(1)}%`}</dd></div>
        <div><dt>Price impact</dt><dd>{row.priceImpactPercentage === null ? "Unavailable" : `${row.priceImpactPercentage.toFixed(2)}%`}</dd></div>
      </dl>
      <details className="row-details">
        <summary>Route, context &amp; failure detail</summary>
        <ExitRowDetails row={row} />
      </details>
    </article>
  );
}

function ExitRowDetails({ row }: { row: ExitLadderRow }) {
  return (
    <div className="exit-row-detail-body">
      <p><strong>Result:</strong> {exitStateLabel(row)}</p>
      {row.failureReason && <p><strong>Why:</strong> {row.failureReason}</p>}
      <p><strong>Route:</strong> {row.route.length ? row.route.join(" → ") : "No route captured"}</p>
      <p><strong>Fees:</strong> {row.poolAndRouteFees}. {row.tokenOrTransferFees}.</p>
      <p><strong>Simulation:</strong> {row.simulationContext}; result {row.simulationResult.replaceAll("_", " ")}.</p>
      <p><strong>Observed:</strong> {formatTime(row.context.observedAt)} via {row.context.provider}.</p>
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail: string;
  tone: string;
}) {
  return (
    <div className={`summary-metric summary-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </div>
  );
}

function SectionLoading({ label, slower = false }: { label: string; slower?: boolean }) {
  return (
    <div className="section-loading" role="status">
      <div className="loading-lens" aria-hidden="true"><span /></div>
      <div>
        <strong>{label}…</strong>
        <p>{slower ? "Checking each sell size can take a moment." : "This result appears as soon as it is ready."}</p>
      </div>
    </div>
  );
}

function SectionError({ title, message }: { title: string; message?: string | null }) {
  return (
    <div className="section-error" role="alert">
      <span aria-hidden="true">?</span>
      <div>
        <strong>{title}</strong>
        <p>{message ?? "This part of the analysis could not be completed."}</p>
        <small>A data-source problem tells us nothing about whether the token is safe, a scam, or sellable.</small>
      </div>
    </div>
  );
}

function strongestAssurance(rows: ExitLadderRow[]): string {
  return assuranceLabel(strongestAssuranceKey(rows));
}

function strongestAssuranceKey(rows: ExitLadderRow[]): AssuranceLevel {
  const rank: AssuranceLevel[] = [
    "unknown",
    "pool_estimate",
    "aggregator_quote",
    "route_simulated",
    "synthetic_simulated",
    "holder_context_simulated",
  ];
  const strongest = rows.reduce<AssuranceLevel>(
    (best, row) => (rank.indexOf(row.assurance) > rank.indexOf(best) ? row.assurance : best),
    "unknown",
  );
  return strongest;
}

function exitTone(report: ExecutableExitReport | null): string {
  if (!report) return "neutral";
  if (report.rows.some((row) => ["poor_proceeds", "no_supported_route", "simulation_failed"].includes(row.state))) return "danger";
  if (report.rows.some((row) => ["provider_unavailable", "provider_rate_limited", "unknown"].includes(row.state))) return "unknown";
  return "calm";
}

function exitRowTone(row: ExitLadderRow): string {
  if (["poor_proceeds", "no_supported_route", "simulation_failed", "insufficient_liquidity"].includes(row.state)) return "danger";
  if (["provider_unavailable", "provider_rate_limited", "unknown", "unsupported"].includes(row.state)) return "unknown";
  return "calm";
}

function exitStateMark(row: ExitLadderRow): string {
  const tone = exitRowTone(row);
  return tone === "calm" ? "✓" : tone === "danger" ? "!" : "?";
}

function exitStateLabel(row: ExitLadderRow): string {
  const labels: Record<string, string> = {
    quoted: "Route quoted; holder context unavailable",
    simulated_success: "Simulation succeeded in the stated context",
    simulation_failed: "Simulation failed",
    holder_context_unavailable: "Holder context unavailable",
    poor_proceeds: "Route exists, but quoted proceeds are poor",
    no_supported_route: "No supported route found for this size",
    insufficient_liquidity: "Insufficient supported liquidity",
    provider_unavailable: "Data source unavailable",
    provider_rate_limited: "Data source was too busy",
    unsupported: "Unsupported mechanism",
    unknown: "Unknown",
  };
  return labels[row.state] ?? row.state.replaceAll("_", " ");
}

function sectionStatus(state: SectionState): string {
  if (state === "loading") return "reading evidence";
  if (state === "error") return "unknown after failure";
  return "not started";
}

function assetTypeLabel(value: TokenIdentity["assetType"]): string {
  const labels: Record<TokenIdentity["assetType"], string> = {
    protocol_receipt: "Protocol receipt",
    governance: "Governance token",
    community_token: "Community token",
    stable_or_wrapped: "Stable / wrapped asset",
    unknown: "Unclassified",
  };
  return labels[value];
}

function identityMetric(report: IdentityReport | null): string {
  if (!report) return "Unknown";
  const labels: Record<IdentityReport["status"], string> = {
    registry_recognized: "Registry match",
    unverified: "Not confirmed",
    unknown: "Unknown",
  };
  return labels[report.status];
}

function dumpMetric(report: DumpCapacityReport | null): string {
  if (!report) return "Unknown";
  return percentageOrUnknown(report.largestWalletShapedSharePercentage);
}

function percentageOrUnknown(value: number | null): string {
  return value === null ? "Unknown" : formatPercentage(value);
}

function controllerGapMetric(unknown: number, notAssessed: number): string {
  const parts = [
    unknown ? `${unknown} unknown` : null,
    notAssessed ? `${notAssessed} not assessed` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "0 evidence gaps";
}

function formatPercentage(value: number): string {
  if (!Number.isFinite(value)) return "Unknown";
  if (value > 0 && value < 0.01) return "<0.01%";
  if (value < 100 && value > 99.99) return ">99.99%";
  return `${value.toLocaleString("en-US", {
    maximumFractionDigits: value < 1 ? 2 : 1,
  })}%`;
}

function holderKindLabel(value: DumpCapacityReport["holders"][number]["authorityKind"]): string {
  const labels: Record<DumpCapacityReport["holders"][number]["authorityKind"], string> = {
    wallet_shaped: "Wallet-shaped",
    token_multisig: "SPL multisig",
    program_controlled: "Program-controlled",
    unresolved: "Unresolved",
    fixture: "Made-up wallet",
  };
  return labels[value];
}

function liquidityControllerKindLabel(
  value: LiquidityControlAnalysis["controllers"][number]["authorityKind"],
): string {
  const labels: Record<
    LiquidityControlAnalysis["controllers"][number]["authorityKind"],
    string
  > = {
    wallet_shaped: "Wallet-shaped",
    token_multisig: "SPL multisig",
    program_controlled: "Program-controlled",
    unresolved: "Unresolved",
    provably_unspendable: "Incinerator",
    fixture: "Made-up authority",
  };
  return labels[value];
}

function controlKindLabel(value: CapabilityFinding["control"]["kind"]): string {
  const labels: Record<CapabilityFinding["control"]["kind"], string> = {
    none: "None identified",
    wallet_shaped: "Wallet-shaped",
    token_multisig: "SPL multisig",
    program_controlled: "Program-controlled",
    unresolved: "Unresolved",
    fixture: "Made-up authority",
  };
  return labels[value];
}

function compactTokenAmount(value: string): string {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value.length > 18 ? `${value.slice(0, 15)}…` : value;
  return new Intl.NumberFormat("en-US", {
    notation: Math.abs(parsed) >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: Math.abs(parsed) < 10 ? 4 : 2,
  }).format(parsed);
}

function boundedJson(value: unknown): string {
  const text = JSON.stringify(value, null, 2) ?? String(value);
  return text.length > 1_200 ? `${text.slice(0, 1_200)}\n…` : text;
}

function usd(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits,
  }).format(value);
}

function compactUsd(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return "Exact amount in details";
  return new Intl.NumberFormat("en-US", {
    notation: value >= 10_000 ? "compact" : "standard",
    maximumFractionDigits: value < 10 ? 4 : 2,
  }).format(value);
}

function formatTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}
